/**
 * Image utility functions for client-side thumbnail generation and compression.
 */

interface CanvasEncodeOptions {
  maxWidthOrHeight?: number;
  quality: number;
  fileType: string;
}

function clampQuality(quality: number): number {
  return Math.max(0, Math.min(1, quality));
}

function calculateDimensions(
  width: number,
  height: number,
  maxWidthOrHeight?: number
): { width: number; height: number } {
  if (!maxWidthOrHeight) {
    return { width, height };
  }

  const largestDimension = Math.max(width, height);
  if (largestDimension <= maxWidthOrHeight) {
    return { width, height };
  }

  const scale = maxWidthOrHeight / largestDimension;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function encodeWithCanvas(file: File, options: CanvasEncodeOptions): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  try {
    const { width, height } = calculateDimensions(
      bitmap.width,
      bitmap.height,
      options.maxWidthOrHeight
    );
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement('canvas'), { width, height });
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('2D canvas context is not available');
    }

    context.drawImage(bitmap, 0, 0, width, height);

    if ('convertToBlob' in canvas) {
      return canvas.convertToBlob({
        type: options.fileType,
        quality: options.quality,
      });
    }

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas encoding returned no data'));
            return;
          }

          resolve(blob);
        },
        options.fileType,
        options.quality
      );
    });
  } finally {
    bitmap.close();
  }
}

/**
 * Generates a thumbnail from an image file.
 * Maintains aspect ratio and does not upscale smaller images.
 *
 * @param file - The image file to generate a thumbnail from
 * @param maxSize - Maximum size in pixels for the largest dimension (default: 200)
 * @returns Promise resolving to a Blob containing the thumbnail
 *
 * @example
 * ```typescript
 * const file = inputElement.files[0];
 * const thumbnail = await generateThumbnail(file, 200);
 * const url = URL.createObjectURL(thumbnail);
 * ```
 */
export async function generateThumbnail(file: File, maxSize: number = 200): Promise<Blob> {
  try {
    return await encodeWithCanvas(file, {
      maxWidthOrHeight: maxSize,
      quality: 0.85,
      fileType: 'image/jpeg',
    });
  } catch (error) {
    throw new Error(`Failed to generate thumbnail: ${error}`, { cause: error });
  }
}

/**
 * Compresses an image file to reduce file size.
 * Maintains original dimensions but applies quality compression.
 *
 * @param file - The image file to compress
 * @param quality - Compression quality from 0 to 1 (default: 0.8)
 * @returns Promise resolving to a Blob containing the compressed image
 *
 * @example
 * ```typescript
 * const file = inputElement.files[0];
 * const compressed = await compressImage(file, 0.7);
 * console.log(`Compressed from ${file.size} to ${compressed.size} bytes`);
 * ```
 */
export async function compressImage(file: File, quality: number = 0.8): Promise<Blob> {
  try {
    return await encodeWithCanvas(file, {
      quality: clampQuality(quality),
      fileType: 'image/jpeg',
    });
  } catch (error) {
    throw new Error(`Failed to compress image: ${error}`, { cause: error });
  }
}

/**
 * Gets the dimensions of an image file without loading the full image.
 *
 * @param file - The image file to get dimensions for
 * @returns Promise resolving to an object with width and height
 */
export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load image: ${error}`));
    };

    img.src = url;
  });
}
